import { useRef } from "react";
import { TrashIcon } from "src/lib/ui-icon-v2";
import { IconButtonV2 } from "../../../IconButtonV2/IconButtonV2.tsx";
import * as styles from "./PreviewRow.style.ts";
import type { ChangeEvent, MouseEvent } from "react";
import type {
  Config,
  MediaObject,
} from "src/components/DropZoneInputV2/types.ts";
import { useDrag, useDrop } from "react-dnd";
import { composeRefs } from "@exonn/hooks";
import Drop from "./drop.svg?react";

type RowProps = {
  onRemove(): void;
  draggedItemId: null | string;
  onChangeDraggedItemId: (draggedItemId: null | string) => void;
  index: number;
  object: MediaObject;
  media: Array<MediaObject>;
  onReplace(newFile: File): void;
  validationRules?: Partial<Config["validationRules"]>;
  onMove: (item: MediaObject, moveToIndex: number) => void;
};

// todo: show uploading progress
export const PreviewRow = (props: RowProps) => {
  const {
    onRemove,
    onReplace,
    onChangeDraggedItemId,
    draggedItemId,
    onMove,
    media,
    object,
    index,
    validationRules,
  } = props;

  const imageName = object.attributes.name;

  const draggable = media.length > 1;

  const url =
    object.attributes.preview_url ||
    object.attributes.original_url ||
    // object.client_preview || it is not working right for .csv files
    undefined;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const accept = validationRules?.accept || [];

  const isDragging = draggedItemId === object.attributes.uuid;
  const localRef = useRef<HTMLDivElement | null>(null);

  const preventReplaceMediaClick = useRef<boolean>(false);

  const [, drag] = useDrag(
    () => ({
      type: "default",
      item: () => {
        onChangeDraggedItemId(object.attributes.uuid);
        preventReplaceMediaClick.current = true;
        return object;
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: () => {
        onChangeDraggedItemId(null);
      },
    }),
    [object, onChangeDraggedItemId],
  );

  const [, drop] = useDrop<MediaObject>(
    () => ({
      accept: "default",
      hover(draggedImage, monitor) {
        if (draggedImage.attributes.uuid === object.attributes.uuid) {
          return;
        }
        const draggedImageIndex = media.findIndex(
          (media) => media.attributes.uuid === draggedImage.attributes.uuid,
        );

        const boundingRect = localRef.current?.getBoundingClientRect();

        if (!boundingRect) {
          return;
        }

        const clientOffset = monitor.getClientOffset();

        if (!clientOffset) {
          return;
        }

        // Moving up
        if (
          draggedImageIndex > index &&
          clientOffset.y >
            boundingRect.bottom - (boundingRect.bottom - boundingRect.top) / 2
        ) {
          return;
        }

        // Moving down
        if (
          draggedImageIndex < index &&
          clientOffset.y <
            boundingRect.top + (boundingRect.bottom - boundingRect.top) / 2
        ) {
          return;
        }

        onMove(draggedImage, index);
      },
    }),
    [onMove, object, index, media],
  );

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.target.files &&
      event.target.files[0] &&
      onReplace(event.target.files[0]);
  };

  const handleReplace = () => {
    if (!preventReplaceMediaClick.current) {
      inputRef.current && inputRef.current.click();
    }
    preventReplaceMediaClick.current = false;
  };

  const handleRemove = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onRemove();
  };

  return (
    <div
      ref={composeRefs(
        (node) => drag(node),
        (node) => drop(node),
        (node) => (localRef.current = node),
      )}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
      onPointerUp={handleReplace}
      css={styles.root({
        isDragging,
      })}
    >
      {draggable && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          title={"Drag"}
          className={"drop"}
          css={styles.drop({
            isDragging,
          })}
        >
          <Drop />
        </button>
      )}

      <input
        type="file"
        accept={accept.length > 0 ? accept.join(",") : undefined}
        hidden
        ref={inputRef}
        onChange={handleFileChange}
      />

      {url && (
        <div css={styles.imageContainer}>
          <img src={url} alt={imageName} css={styles.image} />
        </div>
      )}

      <span css={styles.placeholder}>{imageName}</span>

      <IconButtonV2
        size="sm"
        skin="danger"
        variant="outlined"
        type="button"
        css={styles.removeButton}
        onPointerUp={handleRemove}
      >
        <TrashIcon />
      </IconButtonV2>
    </div>
  );
};
